import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { formHandoffIdentitySchema } from './form-handoff-identity'
import {
  formRequiredBeforeSchema,
  formSignatureImageBase64Schema,
  formSignatureUploadHeaderSchema,
} from './form-workflow-schemas'
import type { FormSnapshot } from './site-form-scoring'
import { toMemberFormDescription } from './site-form-schemas'
import type { PushDataPayload } from '../messaging/push-data-schemas'

export const formHandoffChannelSchema = z
  .enum(['KIOSK', 'MEMBER_APP', 'PUBLIC_LINK', 'STAFF_SCREEN'])
  .register(asEnumType, {
    name: 'FormHandoffChannel',
    description: 'How a form assignment is delivered to the client',
  })

export const formHandoffStatusSchema = z
  .enum([
    'SENT',
    'SHOWN',
    'DELIVERED',
    'OPENED',
    'IN_PROGRESS',
    'COMPLETED',
    'DECLINED',
    'EXPIRED',
    'CANCELLED',
    'FAILED',
  ])
  .register(asEnumType, {
    name: 'FormHandoffStatus',
    description: 'Where one delivery attempt of a form assignment has got to',
  })

export const formHandoffResultCodeSchema = z
  .enum([
    'NOT_ME',
    'IDLE_TIMEOUT',
    'TTL',
    'REROUTED',
    'COMPLETED_ELSEWHERE',
    'STAFF_CANCELLED',
    'ASSIGNMENT_CANCELLED',
    'DEVICE_REVOKED',
    'DEVICE_CAPABILITY_REMOVED',
    'CLIENT_MERGED',
    'PROVIDER_FAILED',
    'NO_DELIVERABLE_CHANNEL',
    'IDENTITY_FAILED',
    'DATE_OF_BIRTH_MISSING',
  ])
  .register(asEnumType, {
    name: 'FormHandoffResultCode',
    description: 'Why a form delivery attempt ended the way it did',
  })

export const formHandoffRequestSourceSchema = z.enum(['STAFF', 'SYSTEM'])

export const FORM_HANDOFF_CHANNEL = formHandoffChannelSchema.enum
export const FORM_HANDOFF_STATUS = formHandoffStatusSchema.enum
export const FORM_HANDOFF_RESULT_CODE = formHandoffResultCodeSchema.enum
export const FORM_HANDOFF_REQUEST_SOURCE = formHandoffRequestSourceSchema.enum

export type FormHandoffChannel = z.infer<typeof formHandoffChannelSchema>
export type FormHandoffStatus = z.infer<typeof formHandoffStatusSchema>
export type FormHandoffResultCode = z.infer<typeof formHandoffResultCodeSchema>
export type FormHandoffRequestSource = z.infer<typeof formHandoffRequestSourceSchema>

export const FORM_HANDOFF_DEVICE_TTL_MS = 10 * 60 * 1000
export const FORM_HANDOFF_REMOTE_TTL_MS = 7 * 24 * 60 * 60 * 1000
export const FORM_HANDOFF_IDLE_TIMEOUT_SECONDS = 120
export const FORM_HANDOFF_MAX_ANSWERS = 200
export const FORM_HANDOFF_MAX_ANSWER_LENGTH = 10000

export const FORM_HANDOFF_TERMINAL_STATUSES: readonly FormHandoffStatus[] = [
  FORM_HANDOFF_STATUS.COMPLETED,
  FORM_HANDOFF_STATUS.DECLINED,
  FORM_HANDOFF_STATUS.EXPIRED,
  FORM_HANDOFF_STATUS.CANCELLED,
  FORM_HANDOFF_STATUS.FAILED,
]

export const FORM_HANDOFF_ACTIVE_STATUSES: readonly FormHandoffStatus[] =
  formHandoffStatusSchema.options.filter(
    (status) => !FORM_HANDOFF_TERMINAL_STATUSES.includes(status)
  )

type ChannelTransitions = {
  forward: readonly FormHandoffStatus[]
  terminal: readonly FormHandoffStatus[]
}

const REMOTE_TRANSITIONS: ChannelTransitions = {
  forward: ['SENT', 'DELIVERED', 'OPENED', 'IN_PROGRESS', 'COMPLETED'],
  terminal: ['EXPIRED', 'CANCELLED', 'FAILED'],
}

export const formHandoffTransitionTable: Record<FormHandoffChannel, ChannelTransitions> = {
  KIOSK: {
    forward: ['SENT', 'SHOWN', 'IN_PROGRESS', 'COMPLETED'],
    terminal: ['DECLINED', 'EXPIRED', 'CANCELLED', 'FAILED'],
  },
  STAFF_SCREEN: {
    forward: ['SENT', 'OPENED', 'IN_PROGRESS', 'COMPLETED'],
    terminal: ['EXPIRED', 'CANCELLED', 'FAILED'],
  },
  MEMBER_APP: REMOTE_TRANSITIONS,
  PUBLIC_LINK: REMOTE_TRANSITIONS,
}

export const isFormHandoffTerminal = (status: FormHandoffStatus): boolean =>
  FORM_HANDOFF_TERMINAL_STATUSES.includes(status)

// Intermediate states may be skipped (completion can arrive before any progress), but a late
// callback can never move an attempt backwards or out of a terminal state.
export const canTransitionFormHandoff = (
  channel: FormHandoffChannel,
  from: FormHandoffStatus,
  to: FormHandoffStatus
): boolean => {
  if (isFormHandoffTerminal(from)) {
    return false
  }
  const { forward, terminal } = formHandoffTransitionTable[channel]
  if (terminal.includes(to)) {
    return true
  }
  const fromIndex = forward.indexOf(from)
  const toIndex = forward.indexOf(to)
  return fromIndex >= 0 && toIndex > fromIndex
}

export const FORM_HANDOFF_PUSH_DEEP_LINK = 'pendingForm'
export const FORM_HANDOFF_PUSH_DATA_VERSION = '1'

// The push payload is readable on the lock screen and by the OS, so it carries routing keys only:
// never a name, an answer, a score or a token.
export const buildMemberFormPushData = (input: {
  formSubmissionId: string
  siteId: string
}): PushDataPayload => ({
  deepLink: FORM_HANDOFF_PUSH_DEEP_LINK,
  formSubmissionId: input.formSubmissionId,
  siteId: input.siteId,
  v: FORM_HANDOFF_PUSH_DATA_VERSION,
})

export const resolveFormHandoffTtlMs = (channel: FormHandoffChannel): number =>
  channel === FORM_HANDOFF_CHANNEL.KIOSK || channel === FORM_HANDOFF_CHANNEL.STAFF_SCREEN
    ? FORM_HANDOFF_DEVICE_TTL_MS
    : FORM_HANDOFF_REMOTE_TTL_MS

const idempotencyKeySchema = z.string().trim().min(1).max(128)

export const createFormHandoffInputSchema = z
  .object({
    siteId: z.string().min(1).max(36),
    clientId: z.string().min(1).max(36),
    siteFormId: z.string().min(1).max(36).nullish(),
    formSubmissionId: z.string().min(1).max(36).nullish(),
    channel: formHandoffChannelSchema,
    deviceTokenId: z.string().min(1).max(36).nullish(),
    requiredBefore: formRequiredBeforeSchema.nullish(),
    idempotencyKey: idempotencyKeySchema,
  })
  .superRefine((input, ctx) => {
    if (!input.siteFormId === !input.formSubmissionId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Send either a form or an outstanding assignment, not both',
        path: ['siteFormId'],
      })
    }
    if ((input.channel === FORM_HANDOFF_CHANNEL.KIOSK) !== !!input.deviceTokenId) {
      ctx.addIssue({
        code: 'custom',
        message: 'A kiosk is required for the kiosk channel and only for it',
        path: ['deviceTokenId'],
      })
    }
  })

export const cancelFormHandoffInputSchema = z.object({
  handoffId: z.uuid(),
  siteId: z.string().min(1).max(36),
})

export const rerouteFormHandoffInputSchema = z
  .object({
    handoffId: z.uuid(),
    siteId: z.string().min(1).max(36),
    channel: formHandoffChannelSchema,
  })
  .refine((input) => input.channel !== FORM_HANDOFF_CHANNEL.KIOSK, {
    message: 'Choose a kiosk by sending the form to it again',
    path: ['channel'],
  })

export const formHandoffSchema = z
  .object({
    handoffId: z.string(),
    siteId: z.string(),
    clientId: z.string(),
    formSubmissionId: z.string(),
    channel: formHandoffChannelSchema,
    deviceTokenId: z.string().nullable(),
    status: formHandoffStatusSchema,
    answeredCount: z.number().int().nullable(),
    questionCount: z.number().int().nullable(),
    statusVersion: z.number().int(),
    expiresAt: z.date(),
    resultCode: formHandoffResultCodeSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    completedAt: z.date().nullable(),
    identityFailedAttempts: z.number().int(),
    identityConfirmedAt: z.date().nullable(),
  })
  .register(asObjectType, {
    name: 'FormHandoff',
    description: 'One delivery attempt of a form assignment, with its live status',
  })

export const formHandoffStateSchema = z.object({
  handoffId: z.string(),
  status: formHandoffStatusSchema,
  statusVersion: z.number().int(),
  resultCode: formHandoffResultCodeSchema.nullable(),
})

const formAnswerInputSchema = z.object({
  siteFormQuestionId: z.string().min(1).max(36),
  answer: z.union([z.string().max(FORM_HANDOFF_MAX_ANSWER_LENGTH), z.boolean()]).nullable(),
})

const formAnswersInputSchema = z.array(formAnswerInputSchema).max(FORM_HANDOFF_MAX_ANSWERS)

export const deviceFormLeaseInputSchema = z.object({
  handoffId: z.uuid(),
  leaseVersion: z.number().int().nonnegative(),
})

export const deviceFormProgressInputSchema = deviceFormLeaseInputSchema.extend({
  answeredCount: z.number().int().nonnegative(),
})

export const deviceFormSubmitInputSchema = deviceFormLeaseInputSchema.extend({
  answers: formAnswersInputSchema,
})

export const deviceFormDeclineInputSchema = deviceFormLeaseInputSchema.extend({
  reason: z.enum([FORM_HANDOFF_RESULT_CODE.NOT_ME, FORM_HANDOFF_RESULT_CODE.DATE_OF_BIRTH_MISSING]),
})

export const deviceFormExpireInputSchema = deviceFormLeaseInputSchema.extend({
  reason: z.literal(FORM_HANDOFF_RESULT_CODE.IDLE_TIMEOUT),
})

export const deviceFormSignatureInitInputSchema = deviceFormLeaseInputSchema.extend({
  siteFormQuestionId: z.string().min(1).max(36),
})

export const deviceFormSignatureFinalizeInputSchema = deviceFormLeaseInputSchema.extend({
  uploadId: z.uuid(),
  signatureImageBase64: formSignatureImageBase64Schema.optional(),
})

export const publicFormProgressInputSchema = z.object({
  answeredCount: z.number().int().nonnegative(),
})

export const publicFormSubmitInputSchema = z.object({
  answers: formAnswersInputSchema,
})

export const publicFormCopyInputSchema = z.object({}).optional()

export const publicFormSignatureInitInputSchema = z.object({
  siteFormQuestionId: z.string().min(1).max(36),
})

export const publicFormSignatureFinalizeInputSchema = z.object({
  uploadId: z.uuid(),
})

export const publicFormQuestionSchema = z.object({
  siteFormQuestionId: z.string(),
  type: z.string(),
  title: z.string(),
  options: z.array(z.string()),
  required: z.boolean(),
  mustAnswerYes: z.boolean(),
  blurbText: z.string().nullable(),
})

export const publicFormProjectionSchema = z.object({
  siteFormId: z.string(),
  revision: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  questions: z.array(publicFormQuestionSchema),
})

export type PublicFormQuestion = z.infer<typeof publicFormQuestionSchema>
export type PublicFormProjection = z.infer<typeof publicFormProjectionSchema>

// Member, device and public readers get an explicit allow-list: points, bands and result mapping
// are staff-only and must never ride along because a field was added to the snapshot later.
export const toPublicFormProjection = (snapshot: FormSnapshot): PublicFormProjection => ({
  siteFormId: snapshot.siteFormId,
  revision: snapshot.revision,
  name: snapshot.name,
  description: toMemberFormDescription(snapshot.description),
  questions: snapshot.questions.map((question) => ({
    siteFormQuestionId: question.siteFormQuestionId,
    type: question.type,
    title: question.title,
    options: question.options,
    required: question.required,
    mustAnswerYes: question.mustAnswerYes,
    blurbText: question.blurbText ?? null,
  })),
})

export const deviceCurrentFormSchema = z.object({
  handoffId: z.string(),
  leaseVersion: z.number().int(),
  statusVersion: z.number().int(),
  expiresAt: z.date(),
  idleTimeoutSeconds: z.number().int(),
  client: z.object({ firstName: z.string().nullable() }),
  identity: formHandoffIdentitySchema,
  form: publicFormProjectionSchema,
})

export const publicFormViewSchema = z.object({
  expiresAt: z.date(),
  answeredCount: z.number().int().nullable(),
  questionCount: z.number().int().nullable(),
  client: z.object({ firstName: z.string().nullable() }),
  form: publicFormProjectionSchema,
})

export const formCompletionReceiptViewSchema = z.object({
  formSubmissionId: z.string(),
  completedAt: z.date(),
  resultLabel: z.string().nullable(),
  resultDescription: z.string().nullable(),
})

export const publicFormReceiptSchema = formCompletionReceiptViewSchema.pick({
  formSubmissionId: true,
  completedAt: true,
})

export const initSignupSignatureUploadInputSchema = z.object({
  siteId: z.string().min(1).max(36),
  siteFormId: z.string().min(1).max(36),
  expectedRevision: z.number().int().positive(),
  siteFormQuestionId: z.string().min(1).max(36),
  signupAttemptId: z.uuid().nullish(),
  intendedClient: z.object({
    firstName: z.string().trim().min(1).max(255),
    lastName: z.string().trim().max(255),
    email: z.string().trim().max(255).nullish(),
    dateOfBirth: z.iso.date().nullish(),
  }),
})

export const signupSignatureUploadTicketSchema = z
  .object({
    signupAttemptId: z.string(),
    reservedSubmissionId: z.string(),
    uploadId: z.string(),
    uploadUrl: z.string(),
    expiresAt: z.date(),
    attemptExpiresAt: z.date(),
    uploadHeaders: z.array(formSignatureUploadHeaderSchema),
  })
  .register(asObjectType, {
    name: 'SignupSignatureUploadTicket',
    description:
      'A short-lived URL for a signature captured while adding a client; pass signupAttemptId with the sign-up answers',
  })

export type CreateFormHandoffInput = z.infer<typeof createFormHandoffInputSchema>
export type CancelFormHandoffInput = z.infer<typeof cancelFormHandoffInputSchema>
export type RerouteFormHandoffInput = z.infer<typeof rerouteFormHandoffInputSchema>
export type FormHandoff = z.infer<typeof formHandoffSchema>
export type FormHandoffState = z.infer<typeof formHandoffStateSchema>
export type DeviceFormLeaseInput = z.infer<typeof deviceFormLeaseInputSchema>
export type DeviceFormProgressInput = z.infer<typeof deviceFormProgressInputSchema>
export type DeviceFormSubmitInput = z.infer<typeof deviceFormSubmitInputSchema>
export type DeviceFormDeclineInput = z.infer<typeof deviceFormDeclineInputSchema>
export type DeviceFormSignatureInitInput = z.infer<typeof deviceFormSignatureInitInputSchema>
export type DeviceFormSignatureFinalizeInput = z.infer<
  typeof deviceFormSignatureFinalizeInputSchema
>
export type PublicFormProgressInput = z.infer<typeof publicFormProgressInputSchema>
export type PublicFormSubmitInput = z.infer<typeof publicFormSubmitInputSchema>
export type PublicFormSignatureInitInput = z.infer<typeof publicFormSignatureInitInputSchema>
export type PublicFormSignatureFinalizeInput = z.infer<
  typeof publicFormSignatureFinalizeInputSchema
>
export type DeviceCurrentForm = z.infer<typeof deviceCurrentFormSchema>
export type PublicFormView = z.infer<typeof publicFormViewSchema>
export type FormCompletionReceiptView = z.infer<typeof formCompletionReceiptViewSchema>
export type PublicFormReceipt = z.infer<typeof publicFormReceiptSchema>
export type InitSignupSignatureUploadInput = z.infer<typeof initSignupSignatureUploadInputSchema>
export type SignupSignatureUploadTicket = z.infer<typeof signupSignatureUploadTicketSchema>
